import {SelectOptionsChanged} from '../Form/SearchableSelects/Event';
import {updateDateFormatHint} from './DateFormatHint';

const SELECTOR_SELECT = '.field-mapping select';
const SELECTOR_CELL   = '.column-mapping-cell';
const SELECTOR_SUBKEY = 'input[data-target="subkey"]';
const SELECTOR_FORM   = 'form[data-subkey-requiring-types]';

const findSubkeyTypes = select => {
    const form = select.closest(SELECTOR_FORM);

    if (!form) {
        return new Set();
    }

    return new Set(form.dataset.subkeyRequiringTypes.split(',').filter(Boolean));
};

const findDateFieldIds = select => {
    const form = select.closest(SELECTOR_FORM);

    if (!form) {
        return new Set();
    }

    return new Set(form.dataset.dateFieldIds.split(',').filter(Boolean));
};

const findWarehouseFieldIds = select => {
    const form = select.closest(SELECTOR_FORM);

    if (!form) {
        return new Set();
    }

    return new Set(form.dataset.warehouseFieldIds.split(',').filter(Boolean));
};

const findUnitFieldIds = select => {
    const form = select.closest(SELECTOR_FORM);

    if (!form) {
        return new Set();
    }

    return new Set(form.dataset.unitFieldIds.split(',').filter(Boolean));
};

const findSlotLabels = element => {
    const form = element.closest(SELECTOR_FORM);

    if (!form) {
        return {};
    }

    return JSON.parse(form.dataset.slotLabels);
};

const findParameterizedFields = element => {
    const form = element.closest(SELECTOR_FORM);

    if (!form) {
        return {};
    }

    return JSON.parse(form.dataset.parameterizedFields);
};

const revealArguments = (cell, parameters) => {
    cell.querySelector('[data-target="field-arguments"]')?.toggleAttribute('hidden', parameters.length === 0);

    cell.querySelectorAll('input[data-target="argument"]').forEach(argument => {
        const position  = Number.parseInt(argument.dataset.position, 10);
        const parameter = parameters[position];

        if (!parameter) {
            argument.setAttribute('hidden', '');
            argument.value = '';

            return;
        }

        argument.placeholder = parameter.placeholder;
        argument.inputMode   = parameter.isNumber ? 'numeric' : 'text';
        argument.removeAttribute('hidden');
    });

    const firstArgument = cell.querySelector('input[data-target="argument"]:not([hidden])');

    if (firstArgument) {
        requestAnimationFrame(() => firstArgument.focus());
    }
};

const fullSlotOptions = new WeakMap();

const readSlotGroup = fieldId => fieldId.slice(0, fieldId.lastIndexOf('.'));

const readSlot = slotSelect => {
    const slot = Number.parseInt(slotSelect.querySelector('select').value, 10);

    return slot;
};

const buildSlotOptions = (slotSelect, labelTemplate, count) => fullSlotOptions
    .get(slotSelect)
    .slice(0, count)
    .map(option => {
        const labeledOption = option.cloneNode(true);

        labeledOption.textContent = labelTemplate.replace('%d', labeledOption.value);

        return labeledOption;
    })
;

const relabelSlotSelect = (slotSelect, labelTemplate, count) => {
    slotSelect.replaceOptions(buildSlotOptions(slotSelect, labelTemplate, count));
    slotSelect.selectValue(String(readSlot(slotSelect)));
};

const renumberSlots = fieldMapping => {
    const slotLabels          = findSlotLabels(fieldMapping);
    const slottedCellsByGroup = new Map();

    fieldMapping.querySelectorAll(SELECTOR_CELL).forEach(cell => {
        const fieldId = cell.querySelector(':scope > od-select select')?.value ?? '';

        if (!Object.hasOwn(slotLabels, fieldId)) {
            return;
        }

        const group = readSlotGroup(fieldId);

        slottedCellsByGroup.set(group, [
            ...(slottedCellsByGroup.get(group) ?? []),
            {
                slotSelect: cell.querySelector('[data-target="field-slot"] od-select'),
                labelTemplate: slotLabels[fieldId],
            },
        ]);
    });

    slottedCellsByGroup.forEach(slottedCells => {
        const slots = [...new Set(slottedCells.map(({slotSelect}) => readSlot(slotSelect)))]
            .sort((left, right) => left - right)
        ;

        slottedCells.forEach(({slotSelect}) => slotSelect.selectValue(String(slots.indexOf(readSlot(slotSelect)) + 1)));
        slottedCells.forEach(({slotSelect, labelTemplate}) => relabelSlotSelect(
            slotSelect,
            labelTemplate,
            slots.length + 1,
        ));
    });
};

const moveToAFreeSlot = (fieldMapping, cell, fieldId) => {
    const slotSelect = cell.querySelector('[data-target="field-slot"] od-select');
    const takenSlots = new Set(
        [...fieldMapping.querySelectorAll(SELECTOR_CELL)]
            .filter(otherCell => otherCell !== cell)
            .filter(otherCell => otherCell.querySelector(':scope > od-select select')?.value === fieldId)
            .map(otherCell => readSlot(otherCell.querySelector('[data-target="field-slot"] od-select'))),
    );

    if (!takenSlots.has(readSlot(slotSelect))) {
        return;
    }

    let freeSlot = 1;

    while (takenSlots.has(freeSlot)) {
        freeSlot++;
    }

    slotSelect.replaceOptions(buildSlotOptions(slotSelect, findSlotLabels(fieldMapping)[fieldId]));
    slotSelect.selectValue(String(freeSlot));
};

const readWarehouseSelect = cell => cell.querySelector('[data-target="field-warehouse"] od-select');

const readWarehouse = cell => readWarehouseSelect(cell).querySelector('select').value;

const findOtherStockCells = (fieldMapping, cell, warehouseFieldIds) => [...fieldMapping.querySelectorAll(SELECTOR_CELL)]
    .filter(otherCell => otherCell !== cell)
    .filter(otherCell => warehouseFieldIds.has(otherCell.querySelector(':scope > od-select select')?.value ?? ''))
;

const unmapColumn = cell => cell.querySelector(':scope > od-select').clear();

const releaseWarehouseHeldElsewhere = (fieldMapping, cell, warehouseFieldIds) => {
    const warehouse = readWarehouse(cell);

    findOtherStockCells(fieldMapping, cell, warehouseFieldIds)
        .filter(otherCell => readWarehouse(otherCell) === warehouse)
        .forEach(unmapColumn);
};

const claimAFreeWarehouse = (fieldMapping, cell, warehouseFieldIds) => {
    const warehouseSelect = readWarehouseSelect(cell);
    const takenWarehouses = new Set(findOtherStockCells(fieldMapping, cell, warehouseFieldIds).map(readWarehouse));

    if (!takenWarehouses.has(readWarehouse(cell))) {
        return;
    }

    const freeOption = warehouseSelect.currentOptions().find(option => !takenWarehouses.has(option.value));

    if (freeOption) {
        warehouseSelect.selectValue(freeOption.value);

        return;
    }

    releaseWarehouseHeldElsewhere(fieldMapping, cell, warehouseFieldIds);
};

export const initializeFieldSlots = root => {
    root.querySelectorAll('[data-target="field-slot"] od-select').forEach(slotSelect => {
        if (fullSlotOptions.has(slotSelect)) {
            return;
        }

        fullSlotOptions.set(slotSelect, slotSelect.currentOptions());
    });

    root.querySelectorAll('[data-component="field-mapping"]').forEach(renumberSlots);
};

const syncOrderMatchInput = (frame, column) => {
    const orderMatchInput = frame.closest(SELECTOR_FORM)?.querySelector('input[name="orderMatchColumn"]');

    if (orderMatchInput) {
        orderMatchInput.value = column;
    }
};

const activateOrderMatch = (frame, column) => {
    frame.querySelector(`.header-cell[data-column="${column}"]`)?.classList.add('order-match-active');

    frame.querySelectorAll(`.preview-cell[data-column="${column}"]`)
        .forEach(previewCell => previewCell.classList.add('order-match-column'));

    syncOrderMatchInput(frame, column);
};

const deactivateOrderMatch = (frame, columnHeader) => {
    columnHeader.classList.remove('order-match-active');

    frame.querySelectorAll(`.preview-cell[data-column="${columnHeader.dataset.column}"]`)
        .forEach(previewCell => previewCell.classList.remove('order-match-column'));

    syncOrderMatchInput(frame, '');

    const fallbackSelect = [...frame.querySelectorAll('.column-mapping-cell select')]
        .find(candidate => candidate.value === 'source_id');

    if (fallbackSelect) {
        activateOrderMatch(frame, fallbackSelect.closest('.column-mapping-cell').dataset.column);
    }
};

document.addEventListener('wizard-step-changed', event => {
    const leftPanel = event.detail.fromPanel;

    if (!leftPanel || leftPanel.dataset.wizardStep !== 'mapping') {
        return;
    }

    const fieldMapping = leftPanel.querySelector('[data-component="field-mapping"]');

    fieldMapping?.dispatchEvent(new CustomEvent('persist-mapping'));
});

/** @param {SelectOptionsChanged} event */
document.addEventListener('selectoptionschanged', event => {
    const select = event.selectContainer.querySelector('select');

    if (!select || !select.matches(SELECTOR_SELECT)) {
        return;
    }

    if (select.closest('[data-target="date-format"]')) {
        updateDateFormatHint(select);

        return;
    }

    if (select.closest('[data-target="field-unit"]')) {
        return;
    }

    if (select.closest('[data-target="field-warehouse"]')) {
        const stockCell = select.closest(SELECTOR_CELL);

        releaseWarehouseHeldElsewhere(
            stockCell.closest('[data-component="field-mapping"]'),
            stockCell,
            findWarehouseFieldIds(select),
        );

        return;
    }

    if (select.closest('[data-target="field-slot"]')) {
        renumberSlots(select.closest('[data-component="field-mapping"]'));

        return;
    }

    const cell = select.closest(SELECTOR_CELL);

    if (!cell) {
        return;
    }

    const fieldMapping   = select.closest('[data-component="field-mapping"]');
    const isSlottedField = Object.hasOwn(findSlotLabels(select), select.value);

    cell.querySelector('[data-target="field-slot"]')?.toggleAttribute('hidden', !isSlottedField);

    if (isSlottedField) {
        moveToAFreeSlot(fieldMapping, cell, select.value);
    }

    renumberSlots(fieldMapping);

    const subkey = cell.querySelector(SELECTOR_SUBKEY);

    if (subkey) {
        if (findSubkeyTypes(select).has(select.value)) {
            subkey.removeAttribute('hidden');
            // Defer past od-select's own post-select this.focus(), which runs later in the same
            // call stack and would otherwise steal focus straight back off the revealed input.
            requestAnimationFrame(() => subkey.focus());
        } else {
            subkey.setAttribute('hidden', '');
            subkey.value = '';
        }
    }

    revealArguments(cell, findParameterizedFields(select)[select.value] ?? []);

    const dateFormat = cell.querySelector('[data-target="date-format"]');

    dateFormat?.toggleAttribute('hidden', !findDateFieldIds(select).has(select.value));

    const warehouseFieldIds = findWarehouseFieldIds(select);
    const isWarehouseField  = warehouseFieldIds.has(select.value);

    cell.querySelector('[data-target="field-warehouse"]')?.toggleAttribute('hidden', !isWarehouseField);

    if (isWarehouseField) {
        claimAFreeWarehouse(fieldMapping, cell, warehouseFieldIds);
    }

    cell.querySelector('[data-target="field-unit"]')?.toggleAttribute('hidden', !findUnitFieldIds(select).has(select.value));

    const frame        = select.closest('[data-component="field-mapping"]');
    const columnHeader = frame?.querySelector(`.header-cell[data-column="${cell.dataset.column}"]`);

    if (!columnHeader) {
        return;
    }

    columnHeader.querySelectorAll('[data-hint-field]')
        .forEach(fieldHint => fieldHint.toggleAttribute('hidden', fieldHint.dataset.hintField !== select.value));

    const skipValue      = select.closest(SELECTOR_FORM)?.dataset.skipValue ?? '';
    const skipped        = select.value === skipValue;
    const mapped         = !skipped && select.value !== '';
    const headerDetected = 'headerDetected' in frame.dataset;

    columnHeader.classList.toggle('header-cell-skipped', skipped);
    columnHeader.classList.toggle('header-cell-mapped', mapped);
    columnHeader.classList.toggle('header-cell-unmapped', headerDetected && !mapped && !skipped);
    cell.classList.toggle('is-skipped', skipped);

    const previewCells = frame.querySelectorAll(`.preview-cell[data-column="${cell.dataset.column}"]`);

    previewCells.forEach(previewCell => previewCell.classList.toggle('column-skipped', skipped));

    if (!mapped && columnHeader.classList.contains('order-match-active')) {
        deactivateOrderMatch(frame, columnHeader);
    }

    if (select.value === 'source_id' && !frame.querySelector('.order-match-active')) {
        activateOrderMatch(frame, cell.dataset.column);
    }
});
